import { Capacitor } from '@capacitor/core'
import { InAppBrowser } from '@capacitor/inappbrowser'
import {
    EVENTR_DOMAIN,
    formatCostString,
    HottelPlannerRoomDetails,
    SPHotelRate,
    SPRoomType,
} from '@eventr/common'
import { ColoursObj, MainButton, Typography } from '@eventr/common-storybook'
import { Box } from '@mui/material'
import { FC } from 'react'
import { useShallow } from 'zustand/react/shallow'
import defaultRoomImage from '../../../assets/pics/HotelThumb0.jpg'
import { EventrGatewayAPI } from '../../../data/gateway.api'
import i18n from '../../../i18n/i18next'
import { bs } from '../../../state/bound.store'
import { useEphemeralStore } from '../../../state/useEphemeralStore'
import { getSubdomain } from '../../../utils'
import { HotelMealCard } from './hotelMealCard'
import { PaymentType } from './paymentType'
import { RefundPolicy } from './refundPolicy'

type THotelAvailCard = {
    rate: SPHotelRate
}

const currencyToLocale = (currency: string) => {
    switch (currency) {
        case 'USD':
            return 'en_US'
        case 'CAD':
            return 'en_CA'
        case 'EUR':
            return 'en_IE'
        case 'GBP':
            return 'en_GB'
        default:
            return 'en_US'
    }
}

export const HotelAvailCard: FC<THotelAvailCard> = ({ rate }) => {
    const { filtersObj } = useEphemeralStore(
        useShallow((s) => {
            return {
                filtersObj: s.filtersObj,
            }
        })
    )

    async function handleBookClick(room: SPRoomType) {
        const user = bs().getCurrentUser()

        const resp = await EventrGatewayAPI.utils.setCache({
            cacheValue: `locale=${currencyToLocale(
                filtersObj.currency
            )}&currency=${filtersObj.currency}&bundle=${
                (room.providerDetails as HottelPlannerRoomDetails).bundle
            }&fullname1=${user.firstName + ' ' + user.lastName}&phone=${
                user.phoneNumber
            }&email=${user.email}`,
        })
        /// goes to a nginx x config thing to split for android and ios for redirect deep links.
        /// otherwise it will redirect back into app and break.
        const url = `https://pr.${getSubdomain()}.${EVENTR_DOMAIN}/hotelCheckout/`
        if (Capacitor.isNativePlatform()) {
            InAppBrowser.openInExternalBrowser({
                url: url + '?cacheKey=' + resp.data.id,
            })
        } else {
            window.open(url + '?cacheKey=' + resp.data.id)
        }
    }

    return (
        <Box display={'grid'}>
            <Box
                sx={{
                    display: 'grid',
                    backgroundColor: ColoursObj.backgrounds.secondary,
                    borderRadius: '8px',
                    border: '1px solid ' + ColoursObj.shades.shade2,
                    padding: '8px',
                    gap: '8px',
                }}
            >
                <Box
                    display={'grid'}
                    gridTemplateColumns={'1fr auto'}
                    gap={'8px'}
                >
                    <Typography variant="bodyBold" lineHeight={'16px'}>
                        {rate.roomTypeName}
                    </Typography>

                    <Box
                        sx={{
                            height: '60px',
                            width: '100px',
                            borderRadius: '8px',
                            backgroundSize: 'cover',
                            backgroundImage:
                                'url(' +
                                (rate.roomTypePhotos
                                    ? rate.roomTypePhotos[0]
                                    : defaultRoomImage) +
                                ')',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                        }}
                    />
                </Box>
                <Box display={'grid'} gap={'8px'}>
                    {rate.types.map((room, index) => {
                        return (
                            <Box
                                key={'room' + index}
                                borderTop={
                                    index !== 0
                                        ? '1px solid ' +
                                          ColoursObj.shades.shade2
                                        : ''
                                }
                            >
                                <Box
                                    display={'grid'}
                                    padding={'8px 0 0 0'}
                                    gridTemplateColumns={'1fr auto'}
                                >
                                    <Box display={'grid'}>
                                        <Typography variant="bodyBold">
                                            {formatCostString(
                                                room.price.perNight,
                                                room.price.currencyCode
                                            )}{' '}
                                            {i18n.t('booking.perNight')}
                                        </Typography>
                                        <Typography>
                                            {formatCostString(
                                                room.price.netAmount,
                                                room.price.currencyCode
                                            )}
                                        </Typography>
                                    </Box>
                                    <MainButton
                                        text={i18n.t('booking.bookNow')}
                                        onClick={() => {
                                            handleBookClick(room)
                                        }}
                                    />
                                </Box>
                                <Box display={'grid'}>
                                    <PaymentType
                                        paymentType={room.paymentType.type}
                                    />
                                    <RefundPolicy
                                        refundPolicy={
                                            room.cancelationPolicy
                                                .freeCancellationBefore
                                        }
                                    />
                                    <HotelMealCard meanStr={room.meal} />
                                </Box>
                            </Box>
                        )
                    })}
                </Box>
            </Box>
        </Box>
    )
}
